import { useCallback, useEffect, useState } from 'react';
import {
  IComposableDashboardCollection,
  IComposableDashboardCollectionsContext,
  IPinnedDashboardCollection,
  IUserDTO,
  assignDashboardCollection,
  getMyPinnedDashboards,
  getMyPinnedDashboardsCollection,
  getPinnedDashboardsCollectionCreatedByCurrentUser,
  pinDashboardCollection,
  unPinDashboardCollection,
  updateDashboardCollection,
} from 'wisestep-api';

import { useUserContext } from '../../../context';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { defaultPinnedDashboardCollection } from '../helper/getDefaultDashboardValues';

import { useComposableDashboardCollectionsContext } from './useComposableDashboardCollectionsContext';

const usePinnedDashboardCollections = (isAssignFlow: boolean) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [openPinDialog, setOpenPinDialog] = useState<boolean>(false);
  const [pinnedDashboards, setPinnedDashboards] = useState<IPinnedDashboardCollection[]>([]);
  const [openShareDialog, setOpenShareDialog] = useState<boolean>(false);
  const fetchPinnedDashboardsCollections = async () => {
    const pinnedDashboardsData = await getPinnedDashboardsCollectionCreatedByCurrentUser();
    setPinnedDashboards(pinnedDashboardsData.data);
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { setDashboardCollections, setSelectedCollection, selectedDashboardCollection } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const [selectedPinnedDashboard, setSelectedPinnedDashboard] =
    useState<IPinnedDashboardCollection>(defaultPinnedDashboardCollection);
  const [isDashboardPin, setIsDashboardPin] = useState(false);

  const updateDashboardStatus = useCallback(
    async (updateDashboardState: IComposableDashboardCollection) => {
      if (!updateDashboardState) {
        setNotification(failed('Some error occurred. Please try again!!'));
        return;
      }
      try {
        const response = isAssignFlow
          ? await assignDashboardCollection(updateDashboardState)
          : await updateDashboardCollection(updateDashboardState);

        const updatedDashboard = isAssignFlow ? response.data?.dashboardCollection : response.data;
        setSelectedCollection(updatedDashboard);
        setDashboardCollections((prev) =>
          prev.map((dashboard) =>
            dashboard.id === updatedDashboard?.id ? updatedDashboard : dashboard
          )
        );
        if (isAssignFlow) {
          fetchPinnedDashboardsCollections();
          setSelectedPinnedDashboard(response?.data?.pinnedDashboardCollection);
        }
        setNotification(
          success(`Dashboard has been ${isAssignFlow ? 'assigned' : 'shared'} successfully!`)
        );
      } catch (error) {
        setNotification(failed('Failed to update the dashboard. Please try again!'));
      }
      openShareDialog && setOpenShareDialog(false);
    },
    [isAssignFlow, openShareDialog, setDashboardCollections, setNotification, setSelectedCollection]
  );

  const handlePinningDashboard = async (
    dashboardId: string,
    payload,
    pinnedDashboardCollectionId?: string
  ) => {
    setOpenPinDialog(false);
    try {
      if (
        (selectedPinnedDashboard?.id &&
          loggedInUser?.email &&
          selectedPinnedDashboard.users?.includes(loggedInUser.email) &&
          selectedDashboardCollection?.id) ||
        pinnedDashboardCollectionId
      ) {
        await unPinDashboardCollection(pinnedDashboardCollectionId ?? selectedPinnedDashboard.id);
        setSelectedPinnedDashboard(defaultPinnedDashboardCollection);
        setNotification(
          success(
            `Dashboard Collection ${
              selectedDashboardCollection?.name ?? ''
            } has been un-pinned from the Home page`
          )
        );
      } else {
        const responseDashboard = await getMyPinnedDashboards();
        const responseCollection = await getMyPinnedDashboardsCollection();
        const pinCount = [...responseDashboard.data, ...responseCollection.data];
        if (
          responseCollection.data?.some(
            (item) =>
              item?.entityId === payload?.entityId && item?.dashboardCollection?.id === dashboardId
          )
        ) {
          setIsDashboardPin(false);
          return;
        }
        if (pinCount?.length < 8) {
          const response = await pinDashboardCollection(dashboardId, payload);
          setSelectedPinnedDashboard(response.data);
          setIsDashboardPin(true);
          setNotification(
            success(
              `Dashboard Collection ${
                selectedDashboardCollection?.name ?? ''
              } has been pinned to the Home page`
            )
          );
        } else {
          setNotification(
            failed(
              `Maximum 8 Dashboards are allowed to pin on the Home page. You can Unpin the current dashboards and add other Analytics dashboards`
            )
          );
        }
      }
      fetchPinnedDashboardsCollections();
    } catch (error) {
      setNotification(failed('Failed to pin the dashboard collection. Please try again!'));
    }
  };

  useEffect(() => {
    fetchPinnedDashboardsCollections();
  }, []);

  return {
    pinnedDashboards,
    selectedPinnedDashboard,
    setSelectedPinnedDashboard,
    updateDashboardStatus,
    openShareDialog,
    setOpenShareDialog,
    handlePinningDashboard,
    openPinDialog,
    setOpenPinDialog,
    isDashboardPin,
  };
};

export default usePinnedDashboardCollections;
